import React, { useState } from 'react';
import { Link, useNavigate } from "react-router-dom";
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Divider from '@mui/material/Divider';
import Settings from '@mui/icons-material/Settings';
import Tune from '@mui/icons-material/Tune';
import Logout from '@mui/icons-material/Logout';
import Avatar from '@mui/material/Avatar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import PoolIcon from '@mui/icons-material/Pool';
import { IsNullOrEmpty } from "../../services/CustomService";
import { getHolidaysMode, setHolidaysMode } from "../../data/accounts";
import { notify, send } from "../../services/NotificationService";

// Avatar + menu du compte connecté (ou lien de connexion).
export default function UserHeader({ hideLoginLink = false }) {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const [holidayDialogOpen, setHolidayDialogOpen] = useState(false);
    const [holidayEnabled, setHolidayEnabled] = useState(false);
    const [holidayStart, setHolidayStart] = useState("");
    const [holidayEnd, setHolidayEnd] = useState("");
    const [holidaySaving, setHolidaySaving] = useState(false);

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const toDateInput = (v) => (v ? String(v).slice(0, 10) : "");

    const openHolidayDialog = () => {
        setHolidayDialogOpen(true);
        getHolidaysMode().then((data) => {
            if (data) {
                setHolidayEnabled(!!data.holidaysMode);
                setHolidayStart(toDateInput(data.startDate));
                setHolidayEnd(toDateInput(data.endDate));
            }
        });
    };

    const saveHoliday = () => {
        if (holidayEnabled && holidayStart && holidayEnd && holidayStart > holidayEnd) {
            send("error", "La date de fin doit être postérieure à la date de début.");
            notify();
            return;
        }
        setHolidaySaving(true);
        setHolidaysMode({
            enabled: holidayEnabled,
            startDate: holidayEnabled && holidayStart ? holidayStart : null,
            endDate: holidayEnabled && holidayEnd ? holidayEnd : null,
        }).then((data) => {
            setHolidaySaving(false);
            if (data) {
                localStorage.setItem("holidaysMode", data.holidaysMode);
                setHolidayDialogOpen(false);
                send("success", data.holidaysMode ? "Mode vacances enregistré." : "Mode vacances désactivé.");
                notify();
            } else {
                send("error", "Une erreur est survenue lors de la modification du mode vacances");
                notify();
            }
        }).catch(() => {
            setHolidaySaving(false);
            send("error", "Une erreur est survenue lors de la modification du mode vacances");
            notify();
        });
    };

    let user = null;
    const stored = localStorage.getItem("user");
    if (IsNullOrEmpty(stored)) {
        localStorage.removeItem("user");
        localStorage.removeItem("holidaysMode");
    } else {
        try {
            user = JSON.parse(stored);
        } catch (error) {
            user = null;
        }
    }

    if (!user || !user.name) {
        if (hideLoginLink) {
            return null;
        }
        return (
            <Link to="/login" className="lk-btn lk-btn--ghost" rel="noopener noreferrer">
                <i className="pi pi-user" aria-hidden="true"></i>
                <span>Connexion</span>
            </Link>
        );
    }

    const userName = user.name;
    const initials = userName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("") || "?";

    return (
        <React.Fragment>
            <button
                type="button"
                className="lk-user"
                onClick={handleClick}
                aria-controls={open ? 'account-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                title={userName}
            >
                <Avatar sx={{ bgcolor: "primary.main", width: 34, height: 34, fontSize: 14, fontWeight: 600 }}>{initials}</Avatar>
                <span className="lk-user__name">{userName}</span>
                <i className="pi pi-angle-down" aria-hidden="true"></i>
            </button>

            <Menu
                anchorEl={anchorEl}
                id="account-menu"
                className="lk-user-menu"
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                slotProps={{
                    paper: {
                        elevation: 0,
                        sx: {
                            minWidth: 220,
                            mt: 1,
                            border: '1px solid #dfe5ee',
                            boxShadow: '0 18px 40px rgba(15,23,42,0.16)',
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <div className="lk-user-menu__head">
                    <strong>{userName}</strong>
                    <small>Espace loueur</small>
                </div>
                <Divider />
                <MenuItem onClick={() => navigate("/moncompte")}>
                    <ListItemIcon>
                        <Settings sx={{ color: "primary.main" }} fontSize="small" />
                    </ListItemIcon>
                    Mon compte
                </MenuItem>
                <MenuItem onClick={() => navigate("/parametres")}>
                    <ListItemIcon>
                        <Tune sx={{ color: "primary.main" }} fontSize="small" />
                    </ListItemIcon>
                    Paramètres
                </MenuItem>
                <MenuItem onClick={openHolidayDialog}>
                    <ListItemIcon>
                        <PoolIcon sx={{ color: "primary.main" }} fontSize="small" />
                    </ListItemIcon>
                    Mode vacances
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => navigate("/logout")}>
                    <ListItemIcon>
                        <Logout sx={{ color: "primary.main" }} fontSize="small" />
                    </ListItemIcon>
                    Déconnexion
                </MenuItem>
            </Menu>

            <Dialog open={holidayDialogOpen} onClose={() => setHolidayDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Mode vacances</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControlLabel
                            control={<Switch checked={holidayEnabled} onChange={(e) => setHolidayEnabled(e.target.checked)} />}
                            label="Activer le mode vacances"
                        />
                        <TextField
                            label="Du"
                            type="date"
                            size="small"
                            value={holidayStart}
                            disabled={!holidayEnabled}
                            onChange={(e) => setHolidayStart(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Au"
                            type="date"
                            size="small"
                            value={holidayEnd}
                            disabled={!holidayEnabled}
                            onChange={(e) => setHolidayEnd(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <Box sx={{ color: "text.secondary", fontSize: 13 }}>
                            Pendant cette période, le traitement automatique des demandes AlloVoisins est suspendu. Laissez les dates vides pour un mode vacances sans échéance.
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHolidayDialogOpen(false)}>Annuler</Button>
                    <Button variant="contained" onClick={saveHoliday} disabled={holidaySaving}>Enregistrer</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
